import type { ChartPoint, ProgressChartModel } from '../types'

/**
 * Where a series sits when all its values are equal: on the baseline when
 * they are all 0 (nothing happened, like every other stat's 0), otherwise
 * mid-chart — a constant 2 is neither the top nor the bottom of anything.
 */
const FLAT_ZERO_POSITION = 0
const FLAT_POSITION = 0.5

const plottedValues = (model: ProgressChartModel): number[] =>
  model.points.map((p) => p.value).filter((v): v is number => v !== null)

/**
 * True when every plotted value is the same (or nothing is plotted): the line
 * carries no trend, so overlaid with other stats it should not fill an area
 * beneath it.
 */
export const isFlatSeries = (model: ProgressChartModel): boolean => {
  const values = plottedValues(model)
  return values.every((value) => value === values[0])
}

/**
 * The model's points with each value mapped to 0..1 between the series' own
 * lowest and highest plotted values (not the chart domain, whose 0 baseline
 * and headroom would flatten it), so several stats with very different
 * ranges each span the chart. Gaps (`null`) stay gaps; only `value` changes.
 */
export const normalizeSeries = (model: ProgressChartModel): ChartPoint[] => {
  const values = plottedValues(model)
  const min = Math.min(...values)
  const span = Math.max(...values) - min
  const flatPosition = min === 0 ? FLAT_ZERO_POSITION : FLAT_POSITION
  return model.points.map((point) => {
    if (point.value === null) {
      return { ...point, value: null }
    }
    return {
      ...point,
      value: span === 0 ? flatPosition : (point.value - min) / span,
    }
  })
}
